import { useEffect } from 'react';

// Module-level (not per-hook-instance) so nested modals compose correctly —
// only the outermost lock actually touches the DOM, inner ones just bump the
// counter and leave it alone; only the last one to unmount restores anything.
let lockCount = 0;
let savedScrollY = 0;

/** Locks page scroll behind a full-screen modal/overlay for as long as it's
 * mounted. `document.scrollingElement` in this app is `<html>`, not `<body>`
 * (confirmed in index.css's `overscroll-behavior-y` comment — no explicit
 * overflow on either, so the browser default standards-mode scroller
 * applies) — a plain `body.style.overflow = 'hidden'` therefore locks
 * nothing, since `body` never actually overflows. On top of locking the
 * real scrolling element, `body` is additionally pinned via
 * `position: fixed` with a negative `top` offset (restored via
 * `window.scrollTo` on unlock) — the standard cross-browser-reliable way to
 * also stop iOS Safari's touch-drag scroll, which plain `overflow: hidden`
 * alone does not reliably block on that engine. Without this, the page
 * underneath keeps scrolling right along with (or independently of, causing
 * visible "double scroll" jitter) the modal's own scrollable content — most
 * noticeable on touch devices, where a finger dragged over the dimmed
 * backdrop scrolls the background page instead of just closing/ignoring the
 * gesture. */
export function useLockBodyScroll(): void {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    if (lockCount === 0) {
      savedScrollY = window.scrollY;
      html.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `-${savedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
    }
    lockCount++;
    return () => {
      lockCount--;
      if (lockCount === 0) {
        html.style.overflow = '';
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        window.scrollTo(0, savedScrollY);
      }
    };
  }, []);
}
